/**
 * Main CanvasMap component - A decoupled, reusable HTML5 Canvas-based map component
 */

import {
  Component,
  createSignal,
  createEffect,
  onMount,
  onCleanup,
  createMemo,
  Show,
  untrack,
  batch,
} from 'solid-js';
import { createStore } from 'solid-js/store';

import type {
  CanvasMapProps,
  CanvasMapState,
  CanvasMapRef,
  CanvasElements,
  PointStyleConfig,
  TrajectoryStyle,
  SpotlightStyle,
  StandalonePoint,
} from './types/interfaces';

import {
  DEFAULT_POINT_STYLES,
  DEFAULT_TRAJECTORY_STYLE,
  DEFAULT_SPOTLIGHT_STYLE,
  DEFAULT_MAP_SIZE,
  MAP_CONSTANTS,
  MAP_Z_INDEX,
} from './types/constants';

import { useSimpleConstraints } from './hooks/useSimpleConstraints';
import {
  handleContainerMouseDown,
  handleContainerMouseMove,
  handleContainerMouseEnter,
  handleContainerMouseLeave,
  handleContainerWheel,
  handleExpandCollapse,
  handleUndo,
  handleHomeIconClick,
  ZoomController,
  setupGlobalMouseListeners,
  type MouseEventOptions,
  type WheelEventOptions,
  type ExpandCollapseCallbacks,
  type UndoCallbacks,
  type HomeIconCallbacks,
} from './events';

import MapCanvas from './components/MapCanvas';
import MapControls from './components/MapControls';
import MapHeader from './components/MapHeader';
import { useComponentRef } from './hooks/useComponentRef';
import { useCoordinateTransforms } from './hooks/useCoordinateTransforms';
import { getAssetIcon } from './utils/assetUtils';
import { preloadFlagIcons } from './utils/canvasUtils';

const CanvasMap: Component<CanvasMapProps> = (props) => {
  // SIMPLIFIED: Use simple constraints hook
  // Use untrack for initial values - hook manages its own internal reactivity via ResizeObserver
  const constraints = useSimpleConstraints(
    untrack(() => props.parentElement),
    untrack(() => props.size || DEFAULT_MAP_SIZE)
  );

  // SIMPLIFIED: Remove userHasMovedMap - always respond to positioning changes

  const [mapState, setMapState] = createStore<CanvasMapState>({
    isVisible: false,
    isDragging: false,
    isResizing: false,
    isPanning: false,
    // Initial position - use untrack since reactive updates are handled in createEffect
    position: untrack(() =>
      constraints.getInitialPosition(
        props.positioning || 'top-right',
        props.customPosition
      )
    ),
    size: untrack(() => props.size || DEFAULT_MAP_SIZE),
    dragStart: { x: 0, y: 0 },
    resizeStart: { x: 0, y: 0, width: 0, height: 0 },
    resizeType: '',
    zoom: 1,
    panOffset: { x: 0, y: 0 },
    panStart: { x: 0, y: 0 },
  });

  const [isHovered, setIsHovered] = createSignal(false);
  const [hoveredPointId, setHoveredPointId] = createSignal<string | null>(null);
  const [isExpanded, setIsExpanded] = createSignal(false);
  const [userHasMovedMap, setUserHasMovedMap] = createSignal(false);
  const [previousPositioning, setPreviousPositioning] =
    createSignal<string>('top-right');

  // Standalone points state management
  const [persistedStandalonePoints, setPersistedStandalonePoints] =
    createSignal<StandalonePoint[]>([]);
  const [currentSessionId, setCurrentSessionId] = createSignal<string | null>(
    null
  );
  const [currentFloorId, setCurrentFloorId] = createSignal<string | null>(null);

  const { getContainerRelativeCoordinates } = useCoordinateTransforms();

  let mapContainer: HTMLDivElement | undefined;
  let mapImage: HTMLImageElement | undefined;
  let mainCanvas: HTMLCanvasElement | undefined;
  let dynamicCanvas: HTMLCanvasElement | undefined;

  // Canvas elements for utilities
  const getCanvasElements = (): CanvasElements => ({
    mainCanvas: mainCanvas || null,
    dynamicCanvas: dynamicCanvas || null,
    mapContainer: mapContainer || null,
    mapImage: mapImage || null,
  });

  // Movement capabilities based on props
  const canMove = (): boolean => {
    const result = props.moveable ?? true;

    return result;
  };
  const canPan = (): boolean => props.pannable ?? true;
  const canZoom = (): boolean => props.zoomable ?? true;
  const canResize = (): boolean => props.resizable ?? true;
  const canExpand = (): boolean => props.expandable ?? true;

  // Merge user styles with defaults
  const pointStyles = createMemo(
    (): PointStyleConfig => ({
      ...DEFAULT_POINT_STYLES,
      ...props.pointStyles,
    })
  );

  const trajectoryStyle = createMemo(
    (): TrajectoryStyle => ({
      ...DEFAULT_TRAJECTORY_STYLE,
      ...props.trajectoryStyle,
    })
  );

  const spotlightStyle = createMemo(
    (): SpotlightStyle => ({
      ...DEFAULT_SPOTLIGHT_STYLE,
      ...props.spotlightStyle,
    })
  );

  // Global event listeners cleanup function
  let removeGlobalListeners: (() => void) | undefined;

  // Expand/collapse callbacks - using getters for reactive values
  const expandCollapseCallbacks: ExpandCollapseCallbacks = {
    calculatePosition: () =>
      constraints.getInitialPosition(
        props.positioning || 'top-right',
        props.customPosition
      ),
    calculateRelativePosition: () => ({ x: 0, y: 0 }),
    setIsExpanded,
    isExpanded,
    onUserMoveReset: () => setUserHasMovedMap(false),
    getParentElement: () => props.parentElement,
  };

  // Undo callbacks - using getters for reactive values
  const undoCallbacks: UndoCallbacks = {
    calculatePosition: () =>
      constraints.getInitialPosition(
        props.positioning || 'top-right',
        props.customPosition
      ),
    calculateRelativePosition: () => ({ x: 0, y: 0 }),
    setIsExpanded,
    onUndoClick: () => props.onUndoClick,
    onUserMoveReset: () => setUserHasMovedMap(false),
  };

  // Session tracking for standalone points persistence
  createEffect(() => {
    const newSessionId = props.currentSessionId;
    const oldSessionId = currentSessionId();

    // If session changed, clear persisted standalone points
    if (newSessionId !== oldSessionId) {
      setCurrentSessionId(newSessionId || null);
      setPersistedStandalonePoints([]);
    }
  });

  // Floor/level tracking for standalone points persistence
  createEffect(() => {
    const newFloorId = props.currentDataSetId;
    const oldFloorId = currentFloorId();

    // If floor changed, clear persisted standalone points
    if (newFloorId !== oldFloorId) {
      setCurrentFloorId(newFloorId || null);
      setPersistedStandalonePoints([]);
    }
  });

  // Update persisted standalone points when new ones are provided
  // Replace (not merge) to ensure changes to staticStations are reflected immediately
  createEffect(() => {
    const newStandalonePoints = props.standalonePoints;
    if (newStandalonePoints !== undefined) {
      setPersistedStandalonePoints(newStandalonePoints);
    }
  });

  // Track session changes for map reset (but not station changes)
  const [previousSessionId, setPreviousSessionId] = createSignal<string | null>(
    null
  );
  const [previousDataSetId, setPreviousDataSetId] = createSignal<string | null>(
    null
  );

  createEffect(() => {
    const currentSessionId = props.currentSessionId;
    const currentDataSetId = props.currentDataSetId;
    const prevSessionId = previousSessionId();
    const prevDataSetId = previousDataSetId();

    // Reset map when session or dataset (floor) changes, but NOT on initial load
    const sessionChanged =
      currentSessionId &&
      currentSessionId !== prevSessionId &&
      prevSessionId !== null;
    const dataSetChanged =
      currentDataSetId &&
      currentDataSetId !== prevDataSetId &&
      prevDataSetId !== null;

    if (sessionChanged || dataSetChanged) {
      // Don't reset if user has moved the map manually
      if (!userHasMovedMap()) {
        revertToOriginalState();
      }
    }

    // Update tracked values
    setPreviousSessionId(currentSessionId || null);
    setPreviousDataSetId(currentDataSetId || null);
  });

  // Component reference - ref is set once during mount, use untrack
  useComponentRef(
    untrack(() => props.ref),
    mapState,
    setMapState,
    expandCollapseCallbacks
  );

  createEffect(() => {
    if (mapState.isVisible && mapContainer) {
      if (removeGlobalListeners) {
        removeGlobalListeners();
      }

      removeGlobalListeners = setupGlobalMouseListeners(
        mapContainer,
        props.points || [],
        getCanvasElements(),
        () => mapState, // Pass getter function
        setMapState,
        getMouseEventOptions(), // Get fresh options with current reactive values
        mapImage,
        persistedStandalonePoints() // Pass standalone points for interaction
      );
    }
  });

  // SIMPLIFIED: Apply constraints when parent bounds change or position updates
  createEffect(() => {
    // Skip if user is interacting with the map
    if (mapState.isDragging || mapState.isResizing || isExpanded()) {
      return;
    }

    // Get current bounds
    const bounds = constraints.parentBounds();

    // Skip if bounds aren't ready
    if (!bounds || bounds.width === 0 || bounds.height === 0) {
      return;
    }

    // Apply constraints to current position
    const constrainedPosition = constraints.constrainPosition(
      mapState.position
    );

    // Only update if position changed significantly
    if (
      Math.abs(constrainedPosition.x - mapState.position.x) > 1 ||
      Math.abs(constrainedPosition.y - mapState.position.y) > 1
    ) {
      setMapState('position', constrainedPosition);
    }
  });

  onMount(() => {
    setMapState('isVisible', true);
    preloadFlagIcons(); // Preload flag icons for better performance
  });

  onCleanup(() => {
    if (removeGlobalListeners) {
      removeGlobalListeners();
    }
  });

  // Track parent element size to detect changes and force top-right positioning
  const [parentSize, setParentSize] = createSignal({
    width: 0,
    height: 0,
  });

  // Centralized function to revert map to original state
  const revertToOriginalState = (): void => {
    batch(() => {
      if (isExpanded()) {
        setIsExpanded(false);
      }

      setMapState({
        size: props.size || DEFAULT_MAP_SIZE,
        zoom: 1,
        panOffset: { x: 0, y: 0 },
        position: constraints.getInitialPosition('top-right'),
      });

      setUserHasMovedMap(false);
    });
  };

  // Respond to positioning changes, but only when appropriate
  createEffect(() => {
    const currentPositioning = props.positioning || 'top-right';
    const currentCustomPosition = props.customPosition;
    const prevPositioning = previousPositioning();

    // If positioning mode changed (e.g., compare mode toggled), reset user movement flag
    if (currentPositioning !== prevPositioning) {
      setPreviousPositioning(currentPositioning);

      // If entering compare mode (custom positioning), collapse the map
      if (currentPositioning === 'custom' && isExpanded()) {
        revertToOriginalState();
      }

      // If exiting compare mode (custom -> top-right), revert to original state
      if (prevPositioning === 'custom' && currentPositioning !== 'custom') {
        revertToOriginalState();
        return; // Exit early since revertToOriginalState handles position update
      }

      // Reset user movement flag for other positioning changes
      setUserHasMovedMap(false);
    }

    // Don't update position if user has manually moved the map OR if map is expanded
    if (userHasMovedMap() || isExpanded()) {
      return;
    }

    // Only reset size/zoom/pan when positioning MODE changes (e.g., compare mode toggle)
    // Don't update position for minor customPosition changes during station navigation
    if (currentPositioning !== prevPositioning) {
      batch(() => {
        if (isExpanded()) {
          setIsExpanded(false);
        }

        setMapState({
          size: props.size || DEFAULT_MAP_SIZE,
          zoom: 1,
          panOffset: { x: 0, y: 0 },
        });

        setUserHasMovedMap(false);

        const newPosition = constraints.getInitialPosition(
          currentPositioning,
          currentCustomPosition
        );
        setMapState('position', newPosition);
      });
    }

    // For customPosition changes without positioning mode change (station navigation):
    // Don't update position - let the map stay where the user positioned it
  });

  // Handle parent element size changes - force reset to top-right when parent resizes
  createEffect(() => {
    if (!props.parentElement) return;

    const checkParentSize = (): void => {
      const rect = props.parentElement!.getBoundingClientRect();
      const newSize = {
        width: rect.width,
        height: rect.height,
      };

      const oldSize = parentSize();
      const hasParentSizeChanged =
        oldSize.width !== 0 &&
        oldSize.height !== 0 && // Skip initial size setting
        (oldSize.width !== newSize.width || oldSize.height !== newSize.height);

      if (hasParentSizeChanged) {
        // Revert to original state on viewport size change
        revertToOriginalState();
      }

      setParentSize(newSize);
    };

    // Set up resize observer to watch parent element
    const resizeObserver = new ResizeObserver(checkParentSize);
    resizeObserver.observe(props.parentElement);

    // Check initial size after a short delay to avoid initial trigger
    const INITIAL_SIZE_CHECK_DELAY_MS = 100;
    const timeoutId = setTimeout(checkParentSize, INITIAL_SIZE_CHECK_DELAY_MS);

    onCleanup(() => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    });
  });

  // Handle zoom controls
  const handleZoomIn = (): void => {
    ZoomController.handleZoomIn(mapState, setMapState, MAP_CONSTANTS.MAX_ZOOM);
  };

  const handleZoomOut = (): void => {
    ZoomController.handleZoomOut(mapState, setMapState, MAP_CONSTANTS.MIN_ZOOM);
  };

  const handleZoomReset = (): void => {
    ZoomController.handleZoomReset(setMapState, MAP_CONSTANTS.MIN_ZOOM);
  };

  // Control for zoom reset button visibility
  const canShowZoomReset = (): boolean => props.showZoomReset ?? false;

  // Control for home icon button visibility
  const canShowHome = (): boolean => props.showHome ?? true;

  // Control for header reset button visibility
  const canShowHeaderReset = (): boolean => props.showHeaderReset ?? false;

  // Mouse event options - create getter to ensure reactivity
  const getMouseEventOptions = (): MouseEventOptions => ({
    moveable: canMove(),
    resizable: canResize(),
    pannable: canPan(),
    adaptivePointDensity: props.adaptivePointDensity ?? true,
    onPointSelect: props.onPointSelect,
    onClusterPillClick: props.onClusterPillClick,
    currentPointId: props.currentPointId,
    pointStyles: pointStyles(),
    parentElement: props.parentElement,
    constraintFn: constraints.constrainPosition,
    onUserMove: () => setUserHasMovedMap(true),
    getContainerRelativeCoordinates,
  });

  // Wheel event options - create getter to ensure reactivity
  const getWheelEventOptions = (): WheelEventOptions => ({
    zoomable: props.zoomable ?? true,
  });

  // Home icon callbacks - create getter to ensure reactivity
  const getHomeIconCallbacks = (): HomeIconCallbacks => ({
    onHomeIconClick: props.onHomeIconClick,
    homeIconName: props.homeIconName,
  });

  return (
    <Show when={mapState.isVisible}>
      <div
        ref={mapContainer}
        class={`interactive-map absolute overflow-hidden bg-white border border-gray-200 rounded-lg shadow-lg select-none ${props.className || ''}`}
        style={{
          left: `${String(mapState.position.x)}px`,
          top: `${String(mapState.position.y)}px`,
          width: `${String(mapState.size.width)}px`,
          height: `${String(mapState.size.height)}px`,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS properties must use kebab-case for SolidJS
          'z-index': String(MAP_Z_INDEX.MAP_CONTAINER),
        }}
        onMouseDown={(event) => {
          handleContainerMouseDown(
            event,
            mapContainer!,
            props.points || [],
            getCanvasElements(),
            mapState,
            setMapState,
            getMouseEventOptions(),
            mapImage,
            persistedStandalonePoints()
          );
        }}
        onMouseMove={(event) => {
          const mouseOptions = getMouseEventOptions();
          handleContainerMouseMove(
            event,
            mapContainer!,
            props.points || [],
            mapState,
            hoveredPointId(),
            setHoveredPointId,
            mapImage,
            mainCanvas,
            {
              currentPointId: mouseOptions.currentPointId,
              adaptivePointDensity: mouseOptions.adaptivePointDensity,
              getContainerRelativeCoordinates,
              pointStyles: mouseOptions.pointStyles,
            },
            persistedStandalonePoints()
          );
        }}
        onWheel={(event) => {
          handleContainerWheel(
            event,
            mapContainer!,
            mapState,
            setMapState,
            getWheelEventOptions()
          );
        }}
        onMouseEnter={() => {
          handleContainerMouseEnter(setIsHovered);
        }}
        onMouseLeave={() => {
          handleContainerMouseLeave(setIsHovered, setHoveredPointId);
        }}
      >
        <MapHeader
          dataSets={props.dataSets}
          currentDataSetId={props.currentDataSetId}
          homeIconName={props.homeIconName}
          canMove={canMove()}
          canShowHome={canShowHome()}
          canShowHeaderReset={canShowHeaderReset()}
          canExpand={canExpand()}
          isExpanded={isExpanded()}
          onDataSetChange={props.onDataSetChange}
          onHomeIconClick={(e) => {
            handleHomeIconClick(e, getHomeIconCallbacks());
          }}
          onUndo={(e) => {
            handleUndo(e, setMapState, undoCallbacks);
          }}
          onExpandCollapse={(e) => {
            handleExpandCollapse(
              e,
              mapState,
              setMapState,
              expandCollapseCallbacks
            );
          }}
          getAssetIcon={getAssetIcon}
        />

        {/* Map Content */}
        <div
          class="map-content"
          style={{
            position: 'relative',
            height: `${String(
              mapState.size.height - MAP_CONSTANTS.HEADER_HEIGHT
            )}px`,
            overflow: 'hidden',
            // Cursor is handled by event handlers for proper point detection
            cursor: 'inherit',
          }}
        >
          {/* Floor Plan Image */}
          {props.floorPlanUrl && (
            <img
              ref={mapImage}
              src={props.floorPlanUrl}
              alt="Floor plan"
              class="absolute inset-0 w-full h-full object-cover origin-center pointer-events-auto"
              style={{
                transform: `scale(${String(mapState.zoom)}) translate(${String(
                  mapState.panOffset.x / mapState.zoom
                )}px, ${String(mapState.panOffset.y / mapState.zoom)}px)`,
              }}
              draggable={false}
              // eslint-disable-next-line @typescript-eslint/no-empty-function -- Empty handler for img events
              onLoad={() => {}}
              // eslint-disable-next-line @typescript-eslint/no-empty-function -- Empty handler for img events
              onError={() => {}}
            />
          )}

          {/* Canvas Layer */}
          <MapCanvas
            points={props.points}
            trajectoryPoints={props.trajectoryPoints}
            standalonePoints={persistedStandalonePoints()}
            currentPointId={props.currentPointId}
            hoveredPointId={hoveredPointId()}
            currentHeading={props.currentHeading}
            currentSpotlightDirection={props.currentSpotlightDirection}
            connectPoints={props.connectPoints ?? false}
            adaptivePointDensity={props.adaptivePointDensity ?? true}
            pointStyles={pointStyles()}
            trajectoryStyle={trajectoryStyle()}
            spotlightStyle={spotlightStyle()}
            mapState={mapState}
            imageElement={mapImage}
            mapContainer={mapContainer}
            onMainCanvasRef={(canvas) => (mainCanvas = canvas)}
            onDynamicCanvasRef={(canvas) => (dynamicCanvas = canvas)}
          />

          {/* Map Controls */}
          <MapControls
            zoom={mapState.zoom}
            minZoom={MAP_CONSTANTS.MIN_ZOOM}
            maxZoom={MAP_CONSTANTS.MAX_ZOOM}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={canShowZoomReset() ? handleZoomReset : undefined}
            zoomable={canZoom()}
            visible={isHovered()}
            showReset={canShowZoomReset()}
          />
        </div>

        {/* Resize Handle */}
        {canResize() && isHovered() && (
          <div
            class="absolute bottom-1 left-0 cursor-sw-resize transition-opacity duration-200 select-none"
            data-resize-handle="true"
          >
            <span
              class="tc-icon-drag-bottom-left-corner icon-font text-[var(--modus-wc-color-base-content,#171C1E)]"
              aria-hidden="true"
              style={{
                width: '16px',
                height: '16px',
                ['font-size']: '16px',
                display: 'inline-block',
              }}
            ></span>
          </div>
        )}
      </div>
    </Show>
  );
};

export default CanvasMap;
export type { CanvasMapProps, CanvasMapRef };
